import { useEffect } from "react";

interface UsePreventDocumentScrollProps {
  disabled?: boolean;
}

export function usePreventDocumentScroll({ disabled = false }: UsePreventDocumentScrollProps = {}) {
  useEffect(() => {
    if (disabled) {
      return;
    }

    let startX = 0;
    let startY = 0;

    // Find the nearest scrollable ancestor for a target element
    const getScrollableAncestor = (el: Element | null): HTMLElement | null => {
      let node: Element | null = el;
      while (node && node !== document.documentElement) {
        if (!(node instanceof HTMLElement)) {
          node = node.parentElement;
          continue;
        }
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScrollY =
          (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight + 1;
        if (canScrollY) return node;
        node = node.parentElement;
      }
      return null;
    };

    // Track touch start position globally
    const onDocTouchStart = (e: TouchEvent) => {
      if (e.touches && e.touches.length > 0) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }
    };

    // Prevent page scroll but allow any element with its own scroll
    const onDocTouchMove = (e: TouchEvent) => {
      const t = e.touches?.[0];
      if (!t) return;
      const currentX = t.clientX;
      const currentY = t.clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY; // >0 dragging down

      // If the gesture is primarily horizontal, don't interfere.
      if (Math.abs(deltaX) > Math.abs(deltaY)) return;
      const targetEl = (e.composedPath?.()[0] as Element) ?? (e.target as Element) ?? null;
      const scrollable = getScrollableAncestor(targetEl);

      // If no scrollable ancestor or it's the page scroller -> block
      const pageScroller = (document.scrollingElement || document.documentElement) as HTMLElement;
      if (
        !scrollable ||
        scrollable === pageScroller ||
        scrollable === document.documentElement ||
        scrollable === document.body
      ) {
        e.preventDefault();
        return;
      }

      // Inside a scrollable element: avoid letting the gesture fall-through to the page when at edges
      const atTop = scrollable.scrollTop <= 0;
      const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;
      const isScrollable = scrollable.scrollHeight > scrollable.clientHeight + 1;
      if (!isScrollable || (atTop && deltaY > 0) || (atBottom && deltaY < 0)) e.preventDefault();
    };

    document.documentElement.addEventListener("touchstart", onDocTouchStart, {
      passive: true,
    });
    document.documentElement.addEventListener("touchmove", onDocTouchMove, {
      passive: false,
    });

    return () => {
      document.documentElement.removeEventListener("touchstart", onDocTouchStart as EventListener);
      document.documentElement.removeEventListener("touchmove", onDocTouchMove as EventListener);
    };
  }, [disabled]);
}
